import React, { useState } from 'react';
import { Modal, Input, Select, Button, Alert } from '../design-system';
import { sendSimpleContactEmail } from '../services/emailService';

interface BudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  nome: string;
  empresa: string;
  telefone: string;
  email: string;
  tipoServico: string;
  prazoDesejado: string;
}

interface FormErrors {
  nome?: string;
  empresa?: string;
  telefone?: string;
  email?: string;
  tipoServico?: string;
  prazoDesejado?: string;
}

const initialFormData: FormData = {
  nome: '',
  empresa: '',
  telefone: '',
  email: '',
  tipoServico: '',
  prazoDesejado: ''
};

export function BudgetModal({ isOpen, onClose }: BudgetModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name: keyof FormData, value: string): string | undefined => {
    switch (name) {
      case 'nome':
        if (!value.trim()) return 'Nome é obrigatório';
        if (value.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';
        break;
      case 'empresa':
        if (!value.trim()) return 'Nome da empresa é obrigatório';
        break;
      case 'email':
        if (!value.trim()) return 'E-mail é obrigatório';
        if (!validateEmail(value)) return 'E-mail inválido';
        break;
      case 'telefone':
        if (!value.trim()) return 'Telefone é obrigatório';
        if (value.replace(/\D/g, '').length < 10) return 'Telefone deve ter pelo menos 10 dígitos';
        break;
      case 'tipoServico':
        if (!value) return 'Selecione o tipo de solução';
        break;
      case 'prazoDesejado':
        if (!value) return 'Selecione o prazo desejado';
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    (Object.keys(formData) as (keyof FormData)[]).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    let processedValue = value;
    if (field === 'telefone') {
      processedValue = formatPhone(value);
    }
    setFormData((prev) => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const result = await sendSimpleContactEmail({
        nome: formData.nome,
        empresa: formData.empresa,
        telefone: formData.telefone,
        email: formData.email,
        tipoSolucao: formData.tipoServico,
        prazoDesejado: formData.prazoDesejado
      });
      if (result.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Solicitar Orçamento" size="lg">
      {submitStatus === 'success' ? (
        <Alert variant="success" title="Solicitação Enviada!">
          Recebemos sua solicitação e entraremos em contato em até 24 horas.
        </Alert>
      ) : submitStatus === 'error' ? (
        <Alert variant="danger" title="Erro no Envio">
          Ocorreu um erro ao enviar sua solicitação. Tente novamente ou entre em contato conosco.
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Nome Completo"
            value={formData.nome}
            onChange={(e) => handleInputChange('nome', e.target.value)}
            onBlur={() => handleInputChange('nome', formData.nome)}
            error={errors.nome}
            placeholder="Seu nome"
            required
            fullWidth
          />
          <Input
            label="Nome da Empresa"
            value={formData.empresa}
            onChange={(e) => handleInputChange('empresa', e.target.value)}
            onBlur={() => handleInputChange('empresa', formData.empresa)}
            error={errors.empresa}
            placeholder="Nome da sua empresa"
            required
            fullWidth
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Telefone/WhatsApp"
            value={formData.telefone}
            onChange={(e) => handleInputChange('telefone', e.target.value)}
            onBlur={() => handleInputChange('telefone', formData.telefone)}
            error={errors.telefone}
            placeholder="(11) 99999-9999"
            required
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleInputChange('email', formData.email)}
            error={errors.email}
            placeholder="seu@email.com"
            required
            fullWidth
          />
        </div>

        <Select
          label="Tipo de Projeto"
          value={formData.tipoServico}
          onChange={(e) => handleInputChange('tipoServico', e.target.value)}
          error={errors.tipoServico}
          options={[
            { value: '', label: 'Selecione o tipo de projeto' },
            { value: 'website', label: 'Site/Website' },
            { value: 'ecommerce', label: 'E-commerce/Loja Online' },
            { value: 'app-mobile', label: 'Aplicativo Mobile' },
            { value: 'sistema-web', label: 'Sistema Web' },
            { value: 'api', label: 'API/Integração' },
            { value: 'automacao', label: 'Automação de Processos' },
            { value: 'consultoria', label: 'Consultoria Tecnológica' },
            { value: 'outro', label: 'Outro (especificar na descrição)' }
          ]}
          required
          fullWidth
        />

        <Select
          label="Prazo Desejado"
          value={formData.prazoDesejado}
          onChange={(e) => handleInputChange('prazoDesejado', e.target.value)}
          error={errors.prazoDesejado}
          options={[
            { value: '', label: 'Selecione o prazo' },
            { value: 'urgente', label: 'Urgente (até 2 semanas)' },
            { value: 'rapido', label: 'Rápido (1 mês)' },
            { value: 'normal', label: 'Normal (2-3 meses)' },
            { value: 'flexivel', label: 'Flexível (3+ meses)' },
            { value: 'nao-sei', label: 'Ainda não sei' }
          ]}
          required
          fullWidth
        />

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Quero Meu Orçamento Agora 🚀'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
